import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { NavigationContext } from '../../context/NavigationContext';
import { toast } from 'react-toastify';
import './ImportList.css';

interface ImportItem {
    id: number;
    state: string;
    filename: string;
    createdAt: string;
}

const ImportListComponent: React.FC = () => {
    const authContext = useContext(AuthContext);
    const navigationContext = useContext(NavigationContext);
    const [imports, setImports] = useState<ImportItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string[]>(['new', 'processing']);

    if (!authContext) {
        throw new Error('ImportListComponent must be used within an AuthProvider');
    }
    if (!navigationContext) {
        throw new Error('ImportListComponent must be used within a NavigationProvider');
    }

    const { token } = authContext;
    const { navigateTo } = navigationContext;

    useEffect(() => {
        const fetchImports = async () => {
            try {
                const response = await fetch('/api/roundtables/imports', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Fehler beim Laden der Imports');
                }

                const data: ImportItem[] = await response.json();
                setImports(data);
                setError(null);
            } catch (error) {
                console.error('Error fetching imports:', error);
                setError(error instanceof Error ? error.message : 'Fehler beim Laden der Imports');
                toast.error(error instanceof Error ? error.message : 'Fehler beim Laden der Imports');
            } finally {
                setLoading(false);
            }
        };

        fetchImports();
    }, [token]);

    const handleViewDetails = (importId: number) => {
        navigateTo(`/imports/tischkonten/${importId}`);
    };

    const handleStatusFilterChange = (status: string) => {
        setStatusFilter(prev => {
            if (prev.includes(status)) {
                return prev.filter(s => s !== status);
            } else {
                return [...prev, status];
            }
        });
    };

    const filteredImports = imports.filter(importItem => 
        statusFilter.length === 0 || statusFilter.includes(importItem.state)
    );

    if (loading) {
        return <div className="import-list-loading">Lade Imports...</div>;
    }

    if (error) {
        return (
            <div className="import-list-container">
                <div className="import-list-error">
                    <h2>Fehler</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="import-list-container">
            <div className="import-list-header">
                <h2>Tischkonten Imports</h2>
                <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 'bold' }}>Status Filter:</span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input 
                            type="checkbox" 
                            checked={statusFilter.includes('new')}
                            onChange={() => handleStatusFilterChange('new')}
                        />
                        <span>Neu</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input 
                            type="checkbox" 
                            checked={statusFilter.includes('processing')}
                            onChange={() => handleStatusFilterChange('processing')}
                        />
                        <span>In Bearbeitung</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input 
                            type="checkbox" 
                            checked={statusFilter.includes('completed')}
                            onChange={() => handleStatusFilterChange('completed')}
                        />
                        <span>Abgeschlossen</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input 
                            type="checkbox" 
                            checked={statusFilter.includes('failed')}
                            onChange={() => handleStatusFilterChange('failed')}
                        />
                        <span>Fehlgeschlagen</span>
                    </label>
                </div>
            </div>
            
            {filteredImports.length === 0 ? (
                <div className="no-imports">
                    <p>{imports.length === 0 ? 'Keine Imports vorhanden' : 'Keine Imports mit den gewählten Status-Filtern gefunden'}</p>
                </div>
            ) : (
                <div className="import-table-wrapper">
                    <table className="import-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Dateiname</th>
                                <th>Status</th>
                                <th>Erstellt am</th>
                                <th>Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredImports.map((importItem) => (
                                <tr key={importItem.id}>
                                    <td>{importItem.id}</td>
                                    <td className="filename-cell">{importItem.filename}</td>
                                    <td>
                                        <span className={`status-badge status-${importItem.state}`}>
                                            {importItem.state}
                                        </span>
                                    </td>
                                    <td>
                                        {new Date(importItem.createdAt).toLocaleString('de-DE', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}
                                    </td>
                                    <td>
                                        <button 
                                            className="details-button"
                                            onClick={() => handleViewDetails(importItem.id)}
                                        >
                                            Details
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ImportListComponent;
